import { useState, useCallback, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getRealm, getStoredAuthRole, isAdminRole } from '../../lib/realm'
import ConfirmModal from '../../components/ConfirmModal'
import useModelDetail from '../../hooks/useModelDetail'
import useModelMedia from '../../hooks/useModelMedia'
import useConnectionEvents from '../../hooks/useConnectionEvents'
import { updateModelAccountDescription, getApiErrorMessage, getModelMediaFolders } from '../../api'
import { listConfigs } from '../../api/graphConfigs'
import ModelDetailHeader from './ModelDetailHeader'
import ModelDetailAccountSection from './ModelDetailAccountSection'
import ModelDetailConnectionHistory from './ModelDetailConnectionHistory'
import ModelDetailMediaSection from './ModelDetailMediaSection'
import ModelBiblePanel from '../../components/ModelBiblePanel'
import { useI18n } from '../../i18n'
import { useToast } from '../../contexts/ToastContext'
import { asList, folderPayload } from '../v2/v2Utils'
import '../../styles/pages/model-detail.css'

export default function ModelDetail() {
  const { t } = useI18n()
  const { addToast } = useToast()
  const { id } = useParams()
  const navigate = useNavigate()
  const [editModalItem, setEditModalItem] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showAccountEdit, setShowAccountEdit] = useState(false)
  const [accountDescriptionDraft, setAccountDescriptionDraft] = useState('')
  const [timezoneDraft, setTimezoneDraft] = useState('')
  const [maxMessagePriceDraft, setMaxMessagePriceDraft] = useState('')
  const [defaultConfigIdDraft, setDefaultConfigIdDraft] = useState('')
  const [savingAccountDescription, setSavingAccountDescription] = useState(false)
  const [configs, setConfigs] = useState([])
  const [mediaFolders, setMediaFolders] = useState([])
  const [mediaFolder, setMediaFolder] = useState('')
  const [mediaLimit, setMediaLimit] = useState('120')
  const [showHiddenMedia, setShowHiddenMedia] = useState(false)

  const {
    model,
    profile,
    account,
    loading,
    loadingAccount,
    error,
    setModel,
    setError,
    handleDelete,
    deleting,
  } = useModelDetail(id)

  const onModelUpdate = useCallback(
    (updates) => {
      if (updates.error !== undefined) setError(updates.error)
      const { error: _err, ...rest } = updates
      if (Object.keys(rest).length) {
        setModel((prev) => (prev ? { ...prev, ...rest } : prev))
      }
    },
    [setModel, setError]
  )

  const loadMediaFolders = useCallback(async () => {
    if (!id) return
    try {
      const result = await getModelMediaFolders(id)
      setMediaFolders(asList(result))
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load content sources'))
    }
  }, [id, setError])

  useEffect(() => {
    if (!id) return
    let alive = true
    setMediaFolder('')
    setMediaFolders([])
    getModelMediaFolders(id)
      .then((result) => {
        if (alive) setMediaFolders(asList(result))
      })
      .catch((err) => {
        if (alive) setError(getApiErrorMessage(err, 'Failed to load content sources'))
      })
    return () => { alive = false }
  }, [id, setError])

  const mediaScopeParams = useMemo(() => ({
    limit: Number(mediaLimit) || 120,
    offset: 0,
    include_hidden: showHiddenMedia,
    ...folderPayload(mediaFolder),
  }), [mediaFolder, mediaLimit, showHiddenMedia])

  const media = useModelMedia(id, model, onModelUpdate, mediaScopeParams)
  const isAdmin = getRealm() === 'admin'
  const currentRole = getStoredAuthRole()
  const canManageModelDefaultConfig = isAdminRole(currentRole) || currentRole === 'tester_plus'
  const { events: connectionEvents, loading: loadingConnectionEvents } = useConnectionEvents(id, { enabled: isAdmin })

  useEffect(() => {
    if (model?.media_sync_status === 'done') loadMediaFolders()
  }, [loadMediaFolders, model?.media_sync_status, model?.media_sync_updated_at])

  useEffect(() => {
    if (!canManageModelDefaultConfig) {
      setConfigs([])
      return undefined
    }
    let alive = true
    listConfigs()
      .then((result) => { if (alive) setConfigs(asList(result)) })
      .catch(() => { if (alive) setConfigs([]) })
    return () => { alive = false }
  }, [canManageModelDefaultConfig])

  const displayName =
    profile?.name ?? profile?.username ?? profile?.display_name ?? profile?.name_original ?? profile?.username_original ?? model?.name
  const avatarUrl = profile?.avatar ?? profile?.avatar_url ?? profile?.avatarThumb ?? profile?.header
  const hasConnectedOnlyfansapi = model?.provider_links?.some(
    (l) => l.provider === 'onlyfansapi' && l.connection_status === 'connected'
  )
  const mediaSyncing = model?.media_sync_status === 'syncing'
  const syncStatusLabel = mediaSyncing
    ? t('modelDetail.sync.progress')
    : model?.media_sync_status === 'done'
      ? t('modelDetail.sync.done')
      : model?.media_sync_status === 'failed'
        ? t('modelDetail.sync.error')
        : null

  const accountUsageOk = !!model?.is_account_ready_for_use
  const accountUsageTitle = accountUsageOk
    ? t('modelDetail.account.ok')
    : t('modelDetail.account.bad.prefix') +
      (() => {
        const hasDesc = (model?.account_description || '').trim()
        const hasTz = (model?.timezone || '').trim()
        if (!hasDesc && !hasTz) return t('modelDetail.account.bad.both')
        if (!hasDesc) return t('modelDetail.account.bad.description')
        return t('modelDetail.account.bad.timezone')
      })()

  async function handleConfirmDelete() {
    const ok = await handleDelete()
    if (ok) {
      setShowDeleteConfirm(false)
      navigate('..', { replace: true })
    }
  }

  async function handleSaveAccountDescription(e) {
    e.preventDefault()
    if (!id) return
    const rawPrice = String(maxMessagePriceDraft ?? '').trim()
    let max_message_price
    if (rawPrice === '') {
      max_message_price = null
    } else {
      const parsed = parseInt(rawPrice, 10)
      if (!Number.isFinite(parsed) || parsed < 3 || parsed > 200) {
        setError(t('modelDetail.account.maxMessagePrice.invalid'))
        return
      }
      max_message_price = parsed
    }
    const defaultConfigId = defaultConfigIdDraft === '' ? null : Number(defaultConfigIdDraft)
    setSavingAccountDescription(true)
    try {
      const payload = {
        description: accountDescriptionDraft,
        timezone: timezoneDraft,
        max_message_price,
      }
      if (canManageModelDefaultConfig) {
        payload.default_config_id = Number.isFinite(defaultConfigId) ? defaultConfigId : null
      }
      const updated = await updateModelAccountDescription(id, payload)
      setModel((prev) => (prev ? { ...prev, ...updated } : updated))
      setShowAccountEdit(false)
      addToast(t('toast.account.descriptionSaved'), 'success')
    } catch (err) {
      const msg = getApiErrorMessage(err, t('modelDetail.account.description.saveError'))
      setError(msg)
    } finally {
      setSavingAccountDescription(false)
    }
  }

  if (loading || !model) {
    return (
      <div className="model-detail-page">
        {loading ? <p className="model-detail-loading">{t('modelDetail.loading')}</p> : null}
        {error ? <div className="model-detail-error">{error}</div> : null}
      </div>
    )
  }

  return (
    <div className="model-detail-page">
      <ModelDetailHeader displayName={displayName} avatarUrl={avatarUrl} error={error} />

      <ModelDetailAccountSection
        model={model}
        account={account}
        loadingAccount={loadingAccount}
        accountUsageOk={accountUsageOk}
        accountUsageTitle={accountUsageTitle}
        showAccountEdit={showAccountEdit}
        setShowAccountEdit={setShowAccountEdit}
        accountDescriptionDraft={accountDescriptionDraft}
        setAccountDescriptionDraft={setAccountDescriptionDraft}
        timezoneDraft={timezoneDraft}
        setTimezoneDraft={setTimezoneDraft}
        maxMessagePriceDraft={maxMessagePriceDraft}
        setMaxMessagePriceDraft={setMaxMessagePriceDraft}
        defaultConfigIdDraft={defaultConfigIdDraft}
        setDefaultConfigIdDraft={setDefaultConfigIdDraft}
        configs={configs}
        canManageModelDefaultConfig={canManageModelDefaultConfig}
        savingAccountDescription={savingAccountDescription}
        onSaveAccountDescription={handleSaveAccountDescription}
      />

      <section className="model-detail-card" aria-label="Model bible">
        <ModelBiblePanel modelId={id} />
      </section>

      {isAdmin && (
        <ModelDetailConnectionHistory events={connectionEvents} loading={loadingConnectionEvents} />
      )}

      <section
        className="model-detail-media model-detail-media--full"
        aria-label={t('media.section.media')}
      >
        <ModelDetailMediaSection
          modelId={id}
          model={model}
          mediaSyncing={mediaSyncing}
          syncStatusLabel={syncStatusLabel}
          hasConnectedOnlyfansapi={hasConnectedOnlyfansapi}
          mediaFolders={mediaFolders}
          mediaFolder={mediaFolder}
          setMediaFolder={setMediaFolder}
          refreshMediaFolders={loadMediaFolders}
          mediaLimit={mediaLimit}
          setMediaLimit={setMediaLimit}
          showHiddenMedia={showHiddenMedia}
          setShowHiddenMedia={setShowHiddenMedia}
          editModalItem={editModalItem}
          setEditModalItem={setEditModalItem}
          {...media}
        />
      </section>

      <ConfirmModal
        open={showDeleteConfirm}
        title={t('modelDetail.account.delete.title')}
        message={t('modelDetail.account.delete.message', { name: model.name })}
        confirmLabel={deleting ? t('common.deleting') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        danger
        confirmDisabled={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => !deleting && setShowDeleteConfirm(false)}
      />
    </div>
  )
}
